import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CHAT_FORM_CONSTANTS, COUNTRIES_LIST } from '../../constant/app.constant';
import chatbotValidation from '../../../assets/static/chatbot-validation.json';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import * as HybridAction from '../../store/actions/hybrid/hybrid.actions';
import * as HybridSelector from '../../store/selectors/hybrid/hybrid.selectors';
import { API } from '../../constant/api.constant';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from 'projects/nga-PAP/src/environments/environment';

/**
 * Interface defining the structure of a country object
 */
interface Country {
  name: string;
  countryCode: string;
  flag: string;
}

/**
 * Component for handling the chatbot login/authentication form
 * Manages user authentication, language selection, and form validation
 */
@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss']
})
export class ChatbotComponent implements OnInit, OnDestroy {
  chatbotForm: FormGroup;
  messages: { text: string; isUser: boolean }[] = [];
  userInput: string = '';
  selectedLanguage: string = 'English';
  languages: string[] = ['English', 'Bahasa (Indonesia)'];
  countries: Country[] = [
    {
      name: 'Indonesia',
      countryCode: '62',
      flag: 'assets/images/indonesia-signup-icon.png'
    }
  ];

  process: string = '';
  public patientauthSub: Subscription;
  public errorSub: Subscription;
  public chatbotConfig: any;
  public customLogo: string;
  public pharmaTagline: string;
  public pharmaTaglineCheck: boolean;
  public useCustomLogo = false;
  public useCustomTagline = false;
  public showError: boolean = false;
  public errorStatus: string = '';
  public pending: boolean = false;

  constructor(
    private fb: FormBuilder, 
    private router: Router,
    private store: Store,
    private translate: TranslateService 
  ) {
    this.chatbotForm = this.fb.group({
      countryCode: [this.countries[0].countryCode, Validators.required],
      mobile: ['', [Validators.required, Validators.pattern('^[0-9]{8,15}$')]],
      email: ['', [Validators.email]],
      consent: [false, Validators.requiredTrue]
    });
    // Set default language
    this.translate.setDefaultLang('en');
    this.translate.use('en');
  }

  // get pharmaCode(): string {
  //   return localStorage.getItem('pharmaCode');
  // }

  ngOnInit(): void {
    this.errorSubscription();
    this.removePatientDetailsLocalStorage();
    if(sessionStorage.getItem(CHAT_FORM_CONSTANTS.PHARMA_CODE_CHATBOT)){
      let pharmaCode = sessionStorage.getItem(CHAT_FORM_CONSTANTS.PHARMA_CODE_CHATBOT);
      this.store.dispatch(HybridAction.loadHybridPharmas({ pharmaCode: pharmaCode }));
    }
    this.loadChatbotConfig();
     this.store.select(HybridSelector.getHybridPharmaDetail).subscribe(data => {
      if (data) {
        const taglineToggle = data?.taglineToggle;
        const tagline = data?.tagline?.trim();
        const logo = data?.logo?.trim();
    
        this.customLogo = logo || '';
        this.pharmaTagline = tagline || '';
    
        if (taglineToggle) {
          // Toggle ON
          this.useCustomLogo = !!logo;
          this.useCustomTagline = !!tagline;
          localStorage.setItem(CHAT_FORM_CONSTANTS.PHARMA_TAGLINE, tagline);
          sessionStorage.setItem(CHAT_FORM_CONSTANTS.PHARMA_LOGO, logo);
        } else {
          this.useCustomLogo = false;
          this.useCustomTagline = false;
        }
      }
    });
  }

  /**
   * Loads chatbot configuration based on process and language
   * Updates form labels and validation messages
   */
  loadChatbotConfig() {
    const process = 'ID_NOV_NR_PND_1'; // or get from localStorage if dynamic
    this.translate.get(`${process}[0]`).subscribe(config => {
      this.chatbotConfig = config;
    });
  }

  /**
   * Getter for mobile number label from config
   * @returns The mobile number label text
   */
  get mobileLabel(): string {
    return this.chatbotConfig?.mobile?.label || '';
  }
  get mobilePlaceholder(): string {
    return this.chatbotConfig?.mobile?.placeholder || '';
  }
  get mobileRequired(): string {
    return this.chatbotConfig?.mobile?.required || '';
  }
  get mobilePatternError(): string {
    return this.chatbotConfig?.mobile?.patternError || '';
  }
  get emailLabel(): string {
    return this.chatbotConfig?.email?.label || '';
  }
  get emailPlaceholder(): string {
    return this.chatbotConfig?.email?.placeholder || '';
  }
  get emailRequired(): string {
    return this.chatbotConfig?.email?.required || '';
  }
  get emailPatternError(): string {
    return this.chatbotConfig?.email?.patternError || '';
  }
  get consentText(): string {
    return this.chatbotConfig?.consent || '';
  }

  get mobileIsRequired(): boolean {
    return this.chatbotConfig?.mobile?.isRequired ?? true;
  }
  get emailIsRequired(): boolean {
    return this.chatbotConfig?.email?.isRequired ?? false;
  }

  get formControls() { return this.chatbotForm.controls; }

  /**
   * Handles language change event
   * Updates translations and reloads configuration
   * @param language - The selected language
   */
  onLanguageChange(language: string): void {
    this.selectedLanguage = language;
    const langCode = language === 'English' ? 'en' : 'id';
    this.translate.use(langCode).subscribe(() => {
      this.loadChatbotConfig();
    });
  }

  /**
   * Handles form submission
   * Validates form and dispatches login action
   * Manages navigation based on user status
   */
  onSubmit(): void {
    if (this.chatbotForm.valid) {
      const formValue = this.chatbotForm.value;
        
      this.store.dispatch(HybridAction.submitLoginData({
        endPoint: API.PATIENT_AUTH,
        formData: {
          phone: formValue.mobile,
          countryCode: formValue.countryCode,
          email: formValue.email,
          process: this.process,
        },
      }));

      this.patientauthSub = this.store.select(HybridSelector.getCreds).subscribe(patientCreds => {
        if (patientCreds !== null) {
          const helper = new JwtHelperService();
          const decodedToken = helper.decodeToken(patientCreds.accessToken);
          if(decodedToken?.data?.consentToken!=null && decodedToken?.data?.consentToken!=''){
            sessionStorage.setItem(CHAT_FORM_CONSTANTS.CONSENT_TOKEN, decodedToken?.data?.consentToken);
          }
          // Use chatbotValidation array to find the first card
          const process = 'ID_NOV_NR_PND_1'; // or get from localStorage if dynamic
          const chatformConfig = chatbotValidation[environment.env][process];
          /**
           * Redirect in case of existing user
           */
          let nextPosition = 0;
          if(decodedToken?.data?.exists == true){
            if(decodedToken?.data?.formId === 0){
              const patientDetailsFormPosition = chatformConfig?.find(c => c.pageName === 'patientDetails')?.position;
              if(patientDetailsFormPosition){
                nextPosition = patientDetailsFormPosition ;
              }
              else{
                /**
                 * Setting Default Patient Details Form Position
                 * Remove if condition is invalid
                 */
                nextPosition = 3;
              }
            }
            else{
              nextPosition = decodedToken?.data?.formId + 1 ;
              const nextCard = chatformConfig?.find(c => c.position === nextPosition);
              if (!nextCard) {
                this.pending = true;
              }
            }
            const nextCard = chatformConfig?.find(c => c.position === nextPosition);
            if (nextCard) {
              this.router.navigate(['enroll', nextCard.cardId, nextCard.pageName]);
            }
          }
          else{
            const firstCard = chatformConfig.find(card => card.position === 1);
            if (firstCard) {
              this.router.navigate(['enroll', firstCard.cardId, firstCard.pageName]);
            }
          }
        }
      });
    } else {
      this.chatbotForm.markAllAsTouched();
    }
  }

  /**
   * Handles mobile number input to remove leading zero
   * @param fieldName - The form field name
   * @param event - The input event
   */
  userInputZero(fieldName: string, event: any): void {
    if (event.target.value) {
      const value = event.target.value;
      if (value[0] === '0') {
        this.chatbotForm.get(fieldName).setValue(value.substring(1));
      }
    }
  }

  /**
   * Restricts input to numbers only
   * @param event - Keyboard event
   * @returns Boolean indicating if input is valid
   */
  onlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  /**
   * Getter for selected country object
   * @returns The selected country object
   */
  get selectedCountry() {
    return this.countries.find(
      c => c.countryCode === this.chatbotForm.get('countryCode')?.value
    );
  }

  /**
   * Prevents space character input
   * @param event - Keyboard event
   * @returns Boolean indicating if input is valid
   */
  preventSpace(event: KeyboardEvent): boolean {
    if (event.key === ' ') {
      event.preventDefault();
      return false;
    }
    return true;
  }

  /**
   * Removes patient details from local storage
   * Cleans up stored form data and authentication tokens
   */
  private removePatientDetailsLocalStorage(): void {
    sessionStorage.removeItem(CHAT_FORM_CONSTANTS.PATIENT_DETAILS);
    sessionStorage.removeItem(CHAT_FORM_CONSTANTS.PATIENT_ID_DETAILS);
    sessionStorage.removeItem(CHAT_FORM_CONSTANTS.ORDER);
    sessionStorage.removeItem(CHAT_FORM_CONSTANTS.PATIENT_AUTH);
    sessionStorage.removeItem(CHAT_FORM_CONSTANTS.CONSENT_TOKEN);
    localStorage.removeItem(CHAT_FORM_CONSTANTS.PATIENT_AUTH);
    sessionStorage.removeItem(CHAT_FORM_CONSTANTS.PHARMA_LOGO);
  }

  /**
   * Sets up subscription for error handling
   * Updates error status and messages based on error codes
   */
  errorSubscription(): void {
    this.errorSub = this.store.select(HybridSelector.getHybridError).subscribe(data => {
      if (data) {
        if (data.code === 'E40107') {
          this.errorStatus = '107';
          this.showError = true;
        } else if (data.code === 'E40108') {
          this.errorStatus = '108';
          this.showError = true;
        }
        else if (data.code === 'E4032') {
          this.errorStatus = '32';
          this.showError = true;
        }
      }
    });
  }

  /**
   * Returns the appropriate status image based on error code
   * @returns Path to the status image
   */
  getStatusImage(): string {
    switch (this.errorStatus) {
      case '107':
        return 'assets/images/alert.svg';
      case '108':
        return 'assets/images/alert.svg';
      case '32':
        return 'assets/images/thumbs-up.svg';
    }
  }

  /**
   * Returns the appropriate status message based on error code
   * @returns Object containing title, message and optional parameters
   */
  getStatusMessage(): { title: string; message: string; params?: any } {
    switch (this.errorStatus) {
      case '107':
        return {
          title: 'ACCOUNT_ALREADY_EXIST',
          message: 'MOBILE_NUMBER_EXIST',
          params: { mobileNumber: this.chatbotForm?.get('mobile')?.value }
        };
      case '108':
        return {
          title: 'ACCOUNT_ALREADY_EXIST',
          message: 'EMAIL_ADDRESS_EXIST',
          params: { emailAddress: this.chatbotForm?.get('email')?.value }
        };
      case '32':
        return {
          title: 'APPLICATION_IN_REVIEW',
          message: 'APPLICATION_IN_REVIEW_MESSAGE',
        };
    }
  }

  ngOnDestroy(): void {
    if (this.patientauthSub) {
      this.patientauthSub.unsubscribe();
    }
    if (this.errorSub) {
      this.errorSub.unsubscribe();
    }
  }
}
